import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import type { APIError, Assistant, Conversation, Message } from '../../../api/types';
import { Chat } from '../../Chat';
import { AssistantListPanel } from '../../panels/AssistantListPanel';
import { ChatHistoryPanel } from '../../panels/ChatHistoryPanel';
import { QuickPromptsPanel } from '../../panels/QuickPromptsPanel';
import { Icon, ErrorDisplay } from '../../shared';
import type { ChatView } from '../../core/ChatNavigation';
import { buildFullExperienceUrl } from '../../../config/fullexperience';
import { useResizable } from '../../../hooks/useResizable';
import { getCSSVariable } from '../../../util/cssVariables';
import styles from './SidebarLayout.module.css';

export interface SidebarLayoutProps {
  messages: Message[];
  streamingMessage: string;
  isSending: boolean;
  isLoading: boolean;
  error?: APIError | null;
  onSendMessage: (message: string) => void;
  onClearError?: () => void;

  assistants?: Assistant[];
  currentAssistantId?: string;
  onSelectAssistant?: (assistantId: string) => void;
  isLoadingAssistants?: boolean;

  conversations?: Conversation[];
  currentConversationId?: string;
  onSelectConversation?: (conversationId: string) => void;
  onNewChat?: () => void;
  isLoadingConversations?: boolean;

  title?: string;
  onExpand?: () => void;
  onRefresh?: () => void;
  showExpand?: boolean;
  showRefresh?: boolean;
  showAssistantsView?: boolean;
  showHistoryView?: boolean;
  showUsageGuidelines?: boolean;
  footerText?: string;
  showClearHistory?: boolean;
  onClearHistory?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  className?: string;

  activeSidebarView?: ChatView;
  onSidebarViewChange?: (view: ChatView) => void;
  sidebarMode?: 'push' | 'overlay';
  onUnpin?: () => void;
  isOpen?: boolean;
  sidebarWidth?: number;
  onSidebarWidthChange?: (width: number) => void;
  enableResize?: boolean;
}

export function SidebarLayout({
  messages,
  streamingMessage,
  isSending,
  isLoading,
  error,
  onSendMessage,
  onClearError,

  assistants = [],
  currentAssistantId,
  onSelectAssistant,
  isLoadingAssistants = false,

  conversations = [],
  currentConversationId,
  onSelectConversation,
  onNewChat,
  isLoadingConversations = false,

  title,
  // onExpand,
  onRefresh,
  // showExpand = true,
  showRefresh = true,
  showAssistantsView = true,
  showHistoryView = true,
  showUsageGuidelines,
  footerText,
  showClearHistory = false,
  onClearHistory,
  onOpenChange,
  className = '',

  activeSidebarView = 'chat',
  onSidebarViewChange,
  sidebarMode = 'overlay',
  onUnpin,
  isOpen: isOpenProp,
  sidebarWidth = 400,
  onSidebarWidthChange,
  enableResize = true,
}: SidebarLayoutProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp ?? false);

  useEffect(() => {
    if (isOpenProp !== undefined) {
      setIsOpen(isOpenProp);
    }
  }, [isOpenProp]);
  const [currentView, setCurrentView] = useState<ChatView>(activeSidebarView);
  const [width, setWidth] = useState(sidebarWidth);

  useEffect(() => {
    setCurrentView(activeSidebarView);
  }, [activeSidebarView]);

  useEffect(() => {
    if (sidebarWidth) {
      setWidth(sidebarWidth);
    }
  }, [sidebarWidth]);

  const constraints = useMemo(
    () => ({
      minWidth: getCSSVariable('--devxgpt-sidebar-min-width', 320),
      maxWidth: getCSSVariable('--devxgpt-sidebar-max-width', 800),
    }),
    []
  );

  const handleWidthChange = useCallback(
    (newSize: { width: number; height: number }) => {
      setWidth(newSize.width);
      onSidebarWidthChange?.(newSize.width);
    },
    [onSidebarWidthChange]
  );

  const { isResizing, handleResizeStart } = useResizable({
    enabled: enableResize,
    size: { width, height: window.innerHeight },
    onSizeChange: handleWidthChange,
    minWidth: constraints.minWidth,
    maxWidth: constraints.maxWidth,
    direction: 'left',
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isOpen, handleClose, handleOpen]);

  const handleSend = useCallback(
    (message: string) => {
      onSendMessage(message);
      if (currentView !== 'chat') {
        setCurrentView('chat');
      }
    },
    [onSendMessage, currentView]
  );

  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      handleSend(prompt);
    },
    [handleSend]
  );

  const handleSelectAssistant = useCallback(
    (assistantId: string) => {
      if (onSelectAssistant) {
        onSelectAssistant(assistantId);
      }
      setCurrentView('chat');
    },
    [onSelectAssistant]
  );

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (onSelectConversation) {
        onSelectConversation(conversationId);
      }
      setCurrentView('chat');
    },
    [onSelectConversation]
  );

  const handleNewChat = useCallback(() => {
    if (onNewChat) {
      onNewChat();
    }
    setCurrentView('chat');
  }, [onNewChat]);

  const handleViewChange = useCallback((view: ChatView) => {
    setCurrentView(view);
    onSidebarViewChange?.(view);
  }, [onSidebarViewChange]);

  // Overlay click handler
  // const handleOverlayClick = useCallback(() => {
  //   handleClose();
  // }, [handleClose]);

  const getHeaderTitle = () => {
    if (title) return title;

    switch (currentView) {
      case 'assistants':
        return 'All Assistants';
      case 'history':
        return 'Chat History';
      case 'chat':
      default:
        return currentAssistantId
          ? assistants.find((a) => a.assistant_id === currentAssistantId)?.title || 'DevXGPT'
          : 'DevXGPT';
    }
  };

  const hasMessages = messages.length > 0 || streamingMessage;
  const currentAssistant = assistants.find((a) => a.assistant_id === currentAssistantId);
  const currentPrompts = currentAssistant?.prompts || [];
  const showQuickPrompts = currentView === 'chat' && !hasMessages && currentPrompts.length > 0;
  
  const latestThreadId = useMemo(() => {
    if (messages.length === 0) return undefined;
    const latestMessage = messages[messages.length - 1];
    return latestMessage.message_metadata?.thread_id;
  }, [messages]);

  const handleOpenFullExperience = useCallback(() => {
    const url = buildFullExperienceUrl({
      view: currentView,
      conversationId: currentConversationId,
      threadId: latestThreadId,
      assistantId: currentAssistantId,
    });
    
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [currentView, currentConversationId, latestThreadId, currentAssistantId]);
  
  // useEffect(() => {
  //   console.log('assistants in SidebarLayout:', assistants);
  // }, [assistants]);

  // Also log in render to see value on every render
  // console.log('assistants in SidebarLayout (render):', assistants);

  const headerActions = (
    <>
      {showRefresh && onRefresh && (currentView === 'assistants' || currentView === 'history') && (
        <button
          className={styles.actionButton}
          onClick={onRefresh}
          aria-label="Refresh"
          title="Refresh"
        >
          <Icon name="refresh" size={16} />
        </button>
      )}
      {onUnpin && (
        <button
          className={styles.actionButton}
          onClick={onUnpin}
          aria-label="Unpin to floating"
          title="Unpin to floating"
        >
          <Icon name="pin-unpin" size={16} />
        </button>
      )}
      {/* {showExpand && onExpand && (
        <button
          className={styles.actionButton}
          onClick={onExpand}
          aria-label="Expand to full page"
          title="Expand to full page"
        >
          <Icon name="expand" size={16} />
        </button>
      )} */}
      {/* {showExpand && onExpand && ( */}
        <button
          className={styles.actionButton}
          onClick={handleOpenFullExperience}
          aria-label="Open full experience"
          title="Open full experience"
        >
          <Icon name="open-in-new" size={16} />
        </button>
      {/* )} */}
    </>
  );

  return (
    <>
      {sidebarMode === 'overlay' && isOpen && (
        // <div
        //   className="devxgpt-overlay"
        //   onClick={handleOverlayClick}
        //   aria-label="Close sidebar"
        //   tabIndex={-1}
        // />
        <></>
      )}
      <div 
        className={`${styles.wrapper} ${className} ${isResizing ? styles.resizing : ''} ${!isOpen ? styles.closed : ''}`}
        style={{ width: `${width}px` }}
      >
        {enableResize && isOpen && (
          <div
            className={styles.resizeHandle}
            onMouseDown={handleResizeStart}
            aria-label="Resize sidebar width"
          />
        )}
        <Chat.Navigation
          currentView={currentView}
          onViewChange={handleViewChange}
          showAssistants={showAssistantsView}
          showHistory={showHistoryView}
          isOpen={isOpen}
          onToggle={handleToggle}
        />

        {isOpen && (
          <div className={styles.container}>
            <Chat
              variant="sidebar"
              conversationId={currentConversationId}
              messages={messages}
              streamingMessage={streamingMessage}
              isSending={isSending}
              isLoading={isLoading}
              error={error}
              assistants={assistants}
              currentAssistantId={currentAssistantId}
            >
              <Chat.Header
                title={getHeaderTitle()}
                onClose={handleClose}
                actions={headerActions}
                // variant="compact"
              />

              {error && <ErrorDisplay error={error} onDismiss={onClearError} />}

              <div className={styles.content}>
                {currentView === 'assistants' && (
                  <AssistantListPanel
                    assistants={assistants}
                    currentAssistantId={currentAssistantId}
                    onSelectAssistant={handleSelectAssistant}
                    isLoading={isLoadingAssistants}
                  />
                )}

                {currentView === 'history' && (
                  <ChatHistoryPanel
                    conversations={conversations}
                    currentConversationId={currentConversationId}
                    onSelectConversation={handleSelectConversation}
                    onNewChat={handleNewChat}
                    isLoading={isLoadingConversations}
                  />
                )}

                {currentView === 'chat' && (
                  <>
                    {showQuickPrompts ? (
                      <QuickPromptsPanel
                        prompts={currentPrompts}
                        onSelectPrompt={handleSelectPrompt}
                        isLoading={isLoading}
                        // title={`Ask ${currentAssistant?.title || 'DevXGPT'}`}
                        title={`${currentAssistant?.title || 'DevXGPT'}`}
                      />
                    ) : (
                      <div className={styles.messagesContainer}>
                        <Chat.Messages
                          messages={messages}
                          isLoading={isLoading}
                          // variant="compact"
                        />
                        {isSending && !streamingMessage && <Chat.TypingIndicator />}
                        {streamingMessage && <Chat.StreamingMessage content={streamingMessage} />}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </>
                )}
              </div>

              {currentView === 'chat' && (
                <Chat.Input
                  onSend={handleSend}
                  disabled={isSending}
                  isSending={isSending}
                  // variant="compact"
                />
              )}

              <Chat.Footer
                showUsageGuidelines={showUsageGuidelines}
                footerText={footerText}
                showClearHistory={showClearHistory && currentView === 'history'}
                onClearHistory={onClearHistory}
                // variant="compact"
              />
            </Chat>
          </div>
        )}
      </div>
    </>
  );
}
